import EventEmitter from './EventEmitter.js';
import DataSet from './DataSet.js';
import Camera from './Camera';
import Physics from './Physics.js';
import Renderer from './Renderer.js';
import Interaction from './Interaction';
import GroupStyleResolver from './GroupStyleResolver.js';

export default class GraphEngine extends EventEmitter {
  constructor(canvas, options) {
    super();

    this._canvas = canvas;
    this._options = options || {};
    this._nodes = new DataSet();
    this._edges = new DataSet();
    this._groupStyleResolver = new GroupStyleResolver(options?.groups);

    this._physics = new Physics(this);
    this._renderer = new Renderer(this);
    this._camera = new Camera(this);
    this._interaction = new Interaction(this);

    // vis-network compatible shape
    this.body = {
      data: {
        nodes: this._nodes,
        edges: this._edges,
      },
      get nodeIndices() {
        return this._engine._nodes.getIds();
      },
      _engine: this,
    };

    if (options?.physics) {
      // Set force parameters only; don't process 'enabled' here.
      // React effects control when physics runs via setOptions({enabled: true}).
      const { enabled, stabilization, solver, ...physicsParams } = options.physics;
      this._physics.setOptions(physicsParams);
    }

    // DataSet mutations → sync physics arrays + single redraw.
    // Physics loop is NOT started here — the consumer (GameGraph) controls
    // when physics runs via setOptions({physics: {enabled: true/false}}).
    const onStructureChange = (evt) => {
      this._physics.syncFromDataSet();
      this.markDirty();
    };
    this._nodes.on('add', onStructureChange);
    this._nodes.on('remove', onStructureChange);
    this._edges.on('add', () => this.markDirty());
    this._edges.on('remove', () => this.markDirty());

    // Visual-only changes (group, color, label) → just redraw, no physics
    this._nodes.on('update', () => this.markDirty());
    this._edges.on('update', () => this.markDirty());

    this._interaction.attach(canvas);
    this._resize();
  }

  _resize() {
    const canvas = this._canvas;
    const parent = canvas.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const w = Math.round(parent.clientWidth);
    const h = Math.round(parent.clientHeight);
    const newW = Math.round(w * dpr);
    const newH = Math.round(h * dpr);
    // Skip if dimensions haven't changed (prevents ResizeObserver loops)
    if (canvas.width === newW && canvas.height === newH) return;
    canvas.width = newW;
    canvas.height = newH;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    this.markDirty();
  }

  markDirty() {
    this._renderer.markDirty();
  }

  // ---- vis-network compatible API ----

  setOptions(opts) {
    if (opts.physics) {
      this._physics.setOptions(opts.physics);
    }
    if (opts.groups) {
      this._groupStyleResolver.setGroups(opts.groups);
      this.markDirty();
    }
    if (opts.edges) {
      this._options.edges = { ...this._options.edges, ...opts.edges };
      this.markDirty();
    }
    if (opts.nodes) {
      this._options.nodes = { ...this._options.nodes, ...opts.nodes };
      this.markDirty();
    }
    if (opts.interaction) {
      this._options.interaction = { ...this._options.interaction, ...opts.interaction };
    }
  }

  redraw() {
    this.markDirty();
  }

  focus(nodeId, opts) {
    this._camera.focus(nodeId, opts);
  }

  fit(opts) {
    this._camera.fit(opts);
  }

  moveTo(opts) {
    this._camera.moveTo(opts);
  }

  selectNodes(ids) {
    this._interaction.selectNodes(ids);
  }

  getSelectedNodes() {
    return this._interaction.getSelectedNodes();
  }

  getPositions(ids) {
    const result = {};
    const nodeIds = ids || this._nodes.getIds();
    const arr = Array.isArray(nodeIds) ? nodeIds : [nodeIds];
    for (let i = 0; i < arr.length; i++) {
      const id = arr[i];
      const idx = this._physics._idToIndex.get(id);
      if (idx !== undefined) {
        result[id] = {
          x: this._physics._pos[idx * 2],
          y: this._physics._pos[idx * 2 + 1],
        };
      }
    }
    return result;
  }

  DOMtoCanvas(domPos) {
    return this._camera.DOMtoCanvas(domPos);
  }

  canvasToDOM(canvasPos) {
    return this._camera.canvasToDOM(canvasPos);
  }

  stabilize() {
    this._physics.stabilize();
  }

  destroy() {
    this._interaction.destroy();
    this._renderer.destroy();
    this._camera.destroy();
    this._physics.destroy();
  }
}
